import React from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, Calendar, ExternalLink, Shirt, Footprints, Cake, Sparkles } from "lucide-react";

const ADDRESS = "Tree House Indoor Play, 1901 N Federal Hwy Suite 104, Pompano Beach, FL";
const MAPS_URL =
    "https://www.google.com/maps/search/?api=1&query=" +
    encodeURIComponent(ADDRESS);
const MAPS_EMBED =
    "https://www.google.com/maps?q=" +
    encodeURIComponent(ADDRESS) +
    "&output=embed";

const InfoRow = ({ icon: Icon, label, value, testid }) => (
    <div data-testid={testid} className="flex items-start gap-4">
        <div className="w-12 h-12 shrink-0 rounded-full bg-secondary border-thick shadow-pop-sm flex items-center justify-center">
            <Icon className="w-5 h-5" strokeWidth={2.5} />
        </div>
        <div>
            <p className="font-heading text-xs uppercase tracking-widest text-foreground/60">
                {label}
            </p>
            <p className="font-heading text-lg sm:text-xl font-bold mt-1">
                {value}
            </p>
        </div>
    </div>
);

export const EventDetails = () => {
    return (
        <section
            id="event-details"
            data-testid="event-details-section"
            className="relative py-20 sm:py-28 px-6 bg-accent text-accent-foreground"
        >
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <span className="inline-block font-heading uppercase tracking-widest text-xs sm:text-sm bg-secondary text-secondary-foreground border-thick rounded-full px-5 py-2 shadow-pop-sm">
                        Detalhes do Evento
                    </span>
                    <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-6">
                        O ponto de encontro
                    </h2>
                    <p className="mt-4 text-lg text-accent-foreground/80 max-w-md">
                        Um espaço seguro, divertido e cheio de brinquedos —
                        perfeito pra correr, brincar e celebrar com o Mickey.
                    </p>

                    <div className="mt-10 space-y-6">
                        <InfoRow
                            icon={Calendar}
                            label="Data"
                            value="12 de Julho"
                            testid="event-date"
                        />
                        <InfoRow
                            icon={Clock}
                            label="Horário"
                            value="1:30 PM"
                            testid="event-time"
                        />
                        <InfoRow
                            icon={MapPin}
                            label="Local"
                            value="Tree House Indoor Play"
                            testid="event-venue"
                        />
                        <div
                            data-testid="event-address"
                            className="bg-white text-foreground border-thick rounded-2xl p-5 shadow-pop-sm"
                        >
                            <p className="font-heading text-sm uppercase tracking-widest text-foreground/60">
                                Endereço
                            </p>
                            <p className="font-heading text-base sm:text-lg font-bold mt-1">
                                1901 N Federal Hwy, Suite 104
                            </p>
                            <p className="font-heading text-base sm:text-lg font-bold">
                                Pompano Beach, FL
                            </p>
                            <a
                                href={MAPS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="event-maps-link"
                                className="mt-3 inline-flex items-center gap-2 font-heading text-sm uppercase tracking-widest underline underline-offset-4"
                            >
                                Abrir no Google Maps{" "}
                                <ExternalLink className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: 30, rotate: 2 }}
                    whileInView={{ opacity: 1, x: 0, rotate: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="relative"
                >
                    <div className="border-thick rounded-3xl overflow-hidden shadow-pop-lg bg-white aspect-square">
                        <iframe
                            title="Tree House Indoor Play Map"
                            src={MAPS_EMBED}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            data-testid="event-map-iframe"
                        />
                    </div>
                    <div className="absolute -bottom-6 -right-4 sm:-right-8 bg-primary text-primary-foreground border-thick rounded-3xl px-5 py-3 shadow-pop rotate-[4deg]">
                        <p className="font-heading text-xs uppercase tracking-widest opacity-80">
                            Vai ser épico
                        </p>
                        <p className="font-heading text-xl font-bold">
                            Bora brincar!
                        </p>
                    </div>
                </motion.div>
            </div>

            {/* Schedule + Dress Code */}
            <div className="max-w-6xl mx-auto mt-20 grid lg:grid-cols-2 gap-8">
                {/* Schedule */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    data-testid="schedule-card"
                    className="bg-white text-foreground border-thick rounded-3xl shadow-pop p-8 sm:p-10"
                >
                    <span className="inline-flex items-center gap-2 font-heading uppercase tracking-widest text-xs bg-secondary text-secondary-foreground border-thick rounded-full px-4 py-1 shadow-pop-sm">
                        <Sparkles className="w-4 h-4" />
                        Programação
                    </span>
                    <h3 className="font-heading text-3xl sm:text-4xl font-bold mt-5">
                        Como vai ser a festa
                    </h3>

                    <ul className="mt-8 space-y-5">
                        {[
                            { t: "1:30 PM", d: "Início da festa — chegada dos convidados", color: "bg-primary text-primary-foreground" },
                            { t: "3:00 PM", d: "Parabéns + visita especial do Mickey 🎂", color: "bg-secondary text-secondary-foreground" },
                            { t: "4:00 PM", d: "Encerramento oficial da festa", color: "bg-accent text-accent-foreground" },
                            { t: "Até 7:00 PM", d: "As crianças podem continuar brincando até o local fechar", color: "bg-white text-foreground" },
                        ].map((item, i) => (
                            <li key={i} className="flex items-start gap-4">
                                <div className={`${item.color} border-thick rounded-2xl shadow-pop-sm px-3 py-2 font-heading font-bold text-sm sm:text-base shrink-0 min-w-[110px] text-center`}>
                                    {item.t}
                                </div>
                                <p className="font-heading text-base sm:text-lg pt-2 leading-snug">
                                    {item.d}
                                </p>
                            </li>
                        ))}
                    </ul>
                </motion.div>

                {/* Dress code */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    data-testid="dresscode-card"
                    className="bg-secondary text-secondary-foreground border-thick rounded-3xl shadow-pop p-8 sm:p-10"
                >
                    <span className="inline-flex items-center gap-2 font-heading uppercase tracking-widest text-xs bg-white text-foreground border-thick rounded-full px-4 py-1 shadow-pop-sm">
                        <Shirt className="w-4 h-4" />
                        O que vestir
                    </span>
                    <h3 className="font-heading text-3xl sm:text-4xl font-bold mt-5">
                        Venha confortável pra brincar
                    </h3>

                    <ul className="mt-8 space-y-5">
                        <li className="flex items-start gap-4 bg-white text-foreground border-thick rounded-2xl shadow-pop-sm p-4">
                            <Shirt className="w-7 h-7 shrink-0 mt-1" strokeWidth={2.5} />
                            <div>
                                <p className="font-heading font-bold text-lg">
                                    Roupa confortável
                                </p>
                                <p className="text-foreground/75 mt-1 text-sm sm:text-base">
                                    Para correr, escalar e brincar à vontade.
                                </p>
                            </div>
                        </li>

                        <li className="flex items-start gap-4 bg-primary text-primary-foreground border-thick rounded-2xl shadow-pop-sm p-4">
                            <Footprints className="w-7 h-7 shrink-0 mt-1" strokeWidth={2.5} />
                            <div>
                                <p className="font-heading font-bold text-lg">
                                    Todos devem entrar de meia
                                </p>
                                <p className="opacity-90 mt-1 text-sm sm:text-base">
                                    Política do espaço — sem sapatos na área de brincar.
                                </p>
                            </div>
                        </li>

                        <li className="flex items-start gap-4 bg-accent text-accent-foreground border-thick rounded-2xl shadow-pop-sm p-4">
                            <Cake className="w-7 h-7 shrink-0 mt-1" strokeWidth={2.5} />
                            <div>
                                <p className="font-heading font-bold text-lg">
                                    Crianças: meias antiderrapantes
                                </p>
                                <p className="opacity-90 mt-1 text-sm sm:text-base">
                                    Pra brincadeira ser ainda mais segura e divertida.
                                </p>
                            </div>
                        </li>
                    </ul>
                </motion.div>
            </div>
        </section>
    );
};

export default EventDetails;
